// useProjectItems — the author's projects as list items, for every surface
// that lists them (the left menu, the Projects page): one projection, so the
// two never drift in name, meta line, actions or which one is open.
//
// A project (Studio v1 D1) comes from the state read (`Library.projects`).
// Opening one records the open (for "Last opened") and opens the Studio on
// it. The open project (`frame.studioProject`, the one the author is in) is
// current in every view, not only while its Studio is shown: it stays open
// in the background (pen "Projects – back to where you were", Decided 19).
// Rename and archive are the project's own commands; after one lands, the
// Library is read again (a refusal shows as a notice). "New project" sets
// up an untitled one, with its starting course, and opens it. Pins, sort and density are the shared
// "projects" list view (useRecentList).

export { restoreProjectNow, useProjectItems };

import { Result } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useState } from "react";
import { projectsOf } from "../../../components/Projects/projects.js";
import type {
    RecentListCommand,
    RecentListView,
} from "../../../components/Sidebar/recentList.js";
import type { RecentItem } from "../../../components/Sidebar/sidebarEntries.js";
import { projectStudioCards } from "../../../components/Studio/studioCards.js";
import {
    emptyLayout,
    placeCards,
} from "../../../components/Studio/studioLayout.js";
import type { StartingCourse } from "../../ports/project.js";
import type { AppPorts, Library, PortResult } from "../../ports.js";
import { useAppShell } from "../context.js";
import { useRecentList } from "./useRecentList.js";

type ProjectItems = {
    // The active projects, and the archived ones (Restore, Delete …).
    readonly items: readonly RecentItem[];
    readonly archived: readonly RecentItem[];
    // "Delete …" asks first (RemoveProjectDialog's props).
    readonly removal: {
        readonly project: {
            readonly title: string;
            readonly archived: boolean;
        } | null;
        readonly onRemove: () => void;
        readonly onArchive: () => void;
        readonly onCancel: () => void;
    };
    // How many cards lie on a project's canvas (0 for an unknown id).
    readonly cardsOf: (id: string) => number;
    readonly view: RecentListView;
    readonly onCommand: (cmd: RecentListCommand) => void;
    // Set up an untitled project and open it.
    readonly newProject: () => void;
};

// The starting course lies at the canvas origin (the app puts it there) and
// is 360 wide; what the first project takes from the desk goes to its right.
const BESIDE_STARTING_COURSE = 360 + 96;

const useProjectItems = (): ProjectItems => {
    const { t } = useLingui();
    const {
        frame,
        library,
        searchResults,
        setLibrary,
        ports,
        dispatch,
        showNotice,
        displayText,
    } = useAppShell();
    const [view, onCommand] = useRecentList("projects");
    // Which project this surface is renaming in place: view-ephemeral and
    // per surface, so the field opens only where the rename was asked for.
    const [renamingId, setRenamingId] = useState<string | null>(null);
    // Which project this surface asks "Delete …?" about (view state).
    const [removingId, setRemovingId] = useState<string | null>(null);
    const current = frame.studioProject;

    const open = (projectId: string) => {
        onCommand({ type: "opened", id: projectId });
        dispatch({ type: "open-studio", projectId });
    };

    // A project command, then the Library read again; a refusal is a notice.
    const run = async <A>(
        write: () => PortResult<A>,
    ): Promise<Result.Result<A, unknown>> => {
        const r = await write();
        if (Result.isErr(r)) {
            showNotice({
                type: "lifecycle-failed",
                message: displayText(r.error),
            });
            return r;
        }
        const lib = await ports.loadLibrary();
        if (Result.isOk(lib)) setLibrary(lib.value);
        return r;
    };

    // Set aside, with an Undo (the notice restores it).
    const archive = (projectId: string, title: string) => {
        run(() => ports.archiveProject(projectId)).then((r) => {
            if (Result.isOk(r))
                showNotice({ type: "project-archived", projectId, title });
        });
    };

    const all = projectsOf(library);
    const projects = all.filter((p) => !p.archived);
    const titleOf = (title: string) =>
        title === "" ? t`Untitled project` : title;
    const items = projects.map((p): RecentItem => {
        const modules = t`${plural(p.modules, {
            one: "# module",
            other: "# modules",
        })}`;
        const cards = t`${plural(p.cards, {
            one: "# card",
            other: "# cards",
        })}`;
        return {
            type: "recent-item",
            list: "projects",
            id: p.id,
            title: titleOf(p.title),
            // "2 modules · 7 cards"
            meta: `${modules} · ${cards}`,
            current: current === p.id,
            open: () => open(p.id),
            rename: () => setRenamingId(p.id),
            archive: () => archive(p.id, titleOf(p.title)),
            confirmDelete: () => setRemovingId(p.id),
            renaming:
                renamingId === p.id
                    ? {
                          onSubmit: (title) => {
                              setRenamingId(null);
                              run(() => ports.renameProject(p.id, title));
                          },
                          onCancel: () => setRenamingId(null),
                      }
                    : undefined,
        };
    });
    const cardsOf = (id: string): number =>
        projects.find((p) => p.id === id)?.cards ?? 0;
    // The parts of the course every new project starts with (PS-43), named
    // here in the author's language and stored as named; the course takes
    // the project's name (the app names it). Whole messages, not "Module {n}": the names are stored, and a message
    // with a placeholder that a catalog lacks renders raw in a production
    // build.
    const startingCourse: StartingCourse = {
        intro: t`Intro`,
        modules: [t`Module 1`, t`Module 2`, t`Module 3`],
    };
    // The first project takes what lay on the one desk before projects
    // existed (a one-off: every top-level card, where the auto-layout puts
    // it, to the right of the starting course), so nothing the author made
    // vanishes from the Studio.
    const firstTakesDesk = (projectId: string) => {
        const cards = projectStudioCards(searchResults, library);
        const places = placeCards(cards, emptyLayout);
        // One after another: each is a command on the same project.
        return [...places].reduce(
            (done: Promise<unknown>, [id, at]) =>
                done.then(() =>
                    ports.placeOnProjectCanvas(projectId, id, {
                        x: at.x + BESIDE_STARTING_COURSE,
                        y: at.y,
                    }),
                ),
            Promise.resolve(),
        );
    };
    const newProject = () => {
        const first = (library?.projects ?? []).length === 0;
        run(() =>
            ports.createProject(t`Untitled project`, startingCourse),
        ).then(async (r) => {
            if (Result.isErr(r)) return;
            if (first) {
                await firstTakesDesk(r.value);
                const lib = await ports.loadLibrary();
                if (Result.isOk(lib)) setLibrary(lib.value);
            }
            open(r.value);
        });
    };
    const archived = all
        .filter((p) => p.archived)
        .map(
            (p): RecentItem => ({
                type: "recent-item",
                list: "projects",
                id: p.id,
                title: titleOf(p.title),
                meta: t`Archived`,
                current: false,
                // An archived project is read-only: restore it to work on it.
                open: () => {
                    run(() => ports.restoreProject(p.id));
                },
                restore: () => {
                    run(() => ports.restoreProject(p.id));
                },
                confirmDelete: () => setRemovingId(p.id),
            }),
        );
    const removing = all.find((p) => p.id === removingId);
    const removal = {
        project:
            removing === undefined
                ? null
                : {
                      title: titleOf(removing.title),
                      archived: removing.archived,
                  },
        onRemove: () => {
            setRemovingId(null);
            if (removing === undefined) return;
            run(() => ports.removeProject(removing.id)).then((r) => {
                if (Result.isOk(r) && frame.studioProject === removing.id)
                    dispatch({ type: "open-projects" });
            });
        },
        onArchive: () => {
            setRemovingId(null);
            if (removing !== undefined)
                archive(removing.id, titleOf(removing.title));
        },
        onCancel: () => setRemovingId(null),
    };
    return { items, archived, removal, cardsOf, view, onCommand, newProject };
};

// The Undo of "Archived …" (the shell's notice): the project back from the
// archive, then the Library read again so it shows at once.
const restoreProjectNow = async (
    ports: AppPorts,
    setLibrary: (library: Library) => void,
    projectId: string,
): PortResult<void> => {
    const r = await ports.restoreProject(projectId);
    const lib = await ports.loadLibrary();
    if (Result.isOk(lib)) setLibrary(lib.value);
    return r;
};
